/**
 * TODO: fix overflow of embed Youtube videos, in GlobalStyles has added a
 * workaround that changes the `iframe` width to 100%
 */
import parse, { domToReact } from 'html-react-parser';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { twJoin } from 'tailwind-merge';

import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import Image from 'components/Image';

import { getArticleImageLink, toFallbackImageAltText } from 'lib/image';
import { forceHttps, sanitize, toInternalLink } from 'lib/links';
import { isTwitterUrlValid, isUrlValidForInternalUsage } from 'lib/string';
import { getYTVideoId } from 'lib/utils';

import {
  doesAnyDomNodeParentContainAttribute,
  doesAnyDomNodeParentContainName,
  isYoutubeIframe,
} from './htmlParserUtils';

import HTML_PARSER from 'constants/htmlParser';
import THIRD_PARTY from 'constants/thirdParty';

const ArticleReadMore = dynamic(() => import('components/Article/ReadMore'));
const ArticleLink = dynamic(() => import('shopper/components/Link'));
const ArticleTable = dynamic(() => import('components/Article/Table'));
const ArticleTableCell = dynamic(() => import('components/Article/TableCell'));
const ArticleTweet = dynamic(() => import('components/Article/Tweet'));
const Divider = dynamic(() => import('components/Divider'));
const ListGroup = dynamic(() => import('components/List/ListGroup'));
const ListItem = dynamic(() => import('components/List/ListItem'));
const YoutubeFacade = dynamic(() => import('components/YoutubeFacade'));
const InstagramEmbed = dynamic(() => import('components/InstagramEmbed'));

const READ_MORE = 'Leia Mais';
const WORDPRESS_CAPTION_MARKUP = '[/caption]';
const WORDPRESS_MARKUPS_TO_REMOVE_FROM_RENDER = [
  '[table',
  '[caption',
  '[gallery',
  'indice:',
];

const articleTextOptions = {
  trim: true,
  replace: (domNode) => {
    const { attribs, children = [], name, parent, data, next } = domNode ?? {};

    if (
      children.length === 0 &&
      ![attribs, name, parent, data, next].some(Boolean)
    ) {
      return <></>;
    }

    if (
      WORDPRESS_MARKUPS_TO_REMOVE_FROM_RENDER.some((wpTag) =>
        data?.trim().startsWith(wpTag)
      )
    ) {
      return <></>;
    }

    if (data?.trim().endsWith(WORDPRESS_CAPTION_MARKUP)) {
      return (
        <Text className="-mt-4 mb-4 text-center" size="size2" italic>
          {data.trim().replace(WORDPRESS_CAPTION_MARKUP, '')}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_FIGCAPTION) {
      return (
        <Text
          as="figcaption"
          className="-mt-4 mb-4 text-center"
          size="size2"
          italic
        >
          {domToReact(children, articleTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_TABLE) {
      return (
        <ArticleTable>{domToReact(children, articleTextOptions)}</ArticleTable>
      );
    }

    if (name === HTML_PARSER.NAME_TABLE_CELL) {
      return (
        <ArticleTableCell>
          {domToReact(children, articleTextOptions)}
        </ArticleTableCell>
      );
    }

    if (name === HTML_PARSER.NAME_HR) {
      return <Divider className="my-4 w-full" />;
    }

    if (name === HTML_PARSER.NAME_BLOCKQUOTE) {
      return (
        <ArticleReadMore>
          {domToReact(children, articleTextOptions)}
        </ArticleReadMore>
      );
    }

    if (name === HTML_PARSER.NAME_PARAGRAPH && attribs.style) {
      return domToReact(children, articleTextOptions);
    }

    if (
      doesAnyDomNodeParentContainName(parent, HTML_PARSER.NAME_BLOCKQUOTE) &&
      name === HTML_PARSER.NAME_STRONG
    ) {
      if (children.length === 0) {
        return <></>;
      }

      return (
        <Heading as="span" className="mb-2" size="size5">
          {domToReact(children, {})}
        </Heading>
      );
    }

    if (
      doesAnyDomNodeParentContainName(parent, HTML_PARSER.NAME_BLOCKQUOTE) &&
      data?.toLowerCase().startsWith(READ_MORE.toLowerCase())
    ) {
      if (children.length === 0) {
        return <></>;
      }

      return (
        <Heading as="span" className="mb-2" size="size5">
          {domToReact(children, {})}
        </Heading>
      );
    }

    /**
     * This attribute is related with a series of old components that are
     * removed due not utilized more. But, for the old articles, we still need
     * render anything instead of components and for this case, we render
     * nothing.
     */
    if (
      doesAnyDomNodeParentContainAttribute({
        attributeValue: HTML_PARSER.WISHLIST_KEYWORD_BLOCK_CLASS_ATTRIBUTE,
        parent,
      })
    ) {
      return <></>;
    }

    if (name === HTML_PARSER.NAME_IMAGE) {
      const { alt, src } = attribs ?? {};

      return (
        <Image
          alt={toFallbackImageAltText(alt)}
          className="mx-auto my-4 h-auto w-full"
          height={192}
          sizes="(max-width: 420px) 400px, (max-width: 768px) 750px, 880px"
          src={forceHttps(getArticleImageLink(src, 400))}
          srcSet={`
            ${forceHttps(getArticleImageLink(src, 400))} 400w,
            ${forceHttps(getArticleImageLink(src, 750))} 750w,
            ${forceHttps(getArticleImageLink(src, 800))} 800w
          `}
          width={343}
        />
      );
    }

    if (name === HTML_PARSER.NAME_EM) {
      return (
        <Text as="em" italic>
          {domToReact(children, articleTextOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_LIST_ITEM) {
      return <ListItem>{domToReact(children, articleTextOptions)}</ListItem>;
    }

    if (
      [HTML_PARSER.NAME_UNORDERED_LIST, HTML_PARSER.NAME_ORDERED_LIST].includes(
        name
      )
    ) {
      const isOrderedList = children[0].name === HTML_PARSER.NAME_ORDERED_LIST;

      return (
        <ListGroup
          className="mb-1 space-y-2 p-2"
          type={isOrderedList ? 'ordered' : 'unordered'}
        >
          {domToReact(children, articleTextOptions)}
        </ListGroup>
      );
    }

    if (
      name === HTML_PARSER.NAME_H2 &&
      children[0]?.name === HTML_PARSER.NAME_SPAN &&
      children[0].children[0]?.data
    ) {
      return (
        <Heading as="h2" className="mb-2 mt-6" size="size3" data-heading>
          {children[0].children?.[0].data}
        </Heading>
      );
    }

    if (name === HTML_PARSER.NAME_H2) {
      return (
        <Heading as="h2" className="mb-2 mt-6" size="size3" data-heading>
          {domToReact(children, articleTextOptions)}
        </Heading>
      );
    }

    if (
      name === HTML_PARSER.NAME_H3 &&
      children[0]?.name === HTML_PARSER.NAME_SPAN &&
      children[0].children[0]?.data
    ) {
      return (
        <Heading as="h3" className="mb-2 mt-6" size="size4" data-heading>
          {children[0].children?.[0].data}
        </Heading>
      );
    }

    if (name === HTML_PARSER.NAME_H3) {
      return (
        <Heading as="h3" className="mb-2 mt-6" size="size4" data-heading>
          {domToReact(children, articleTextOptions)}
        </Heading>
      );
    }

    if (
      name === HTML_PARSER.NAME_H4 &&
      children[0]?.name === HTML_PARSER.NAME_SPAN &&
      children[0].children[0]?.data
    ) {
      return (
        <Heading as="h4" className="mb-2 mt-6" size="size4" data-heading>
          {children[0].children?.[0].data}
        </Heading>
      );
    }

    if (name === HTML_PARSER.NAME_H4) {
      return (
        <Heading as="h4" className="mb-2 mt-6" size="size4" data-heading>
          {domToReact(children, articleTextOptions)}
        </Heading>
      );
    }

    if (name === HTML_PARSER.NAME_PARAGRAPH) {
      if (
        doesAnyDomNodeParentContainName(parent, HTML_PARSER.NAME_BLOCKQUOTE) ||
        children[0]?.data?.startsWith(READ_MORE)
      ) {
        return domToReact(children, articleTextOptions);
      }

      if (children[0]?.name === HTML_PARSER.NAME_IMAGE) {
        return (
          <div className="my-4 flex">
            {domToReact(children, articleTextOptions)}
          </div>
        );
      }

      return (
        <Text className="my-4">{domToReact(children, articleTextOptions)}</Text>
      );
    }

    if (
      name === HTML_PARSER.NAME_HREF &&
      children[0]?.name !== HTML_PARSER.NAME_IMAGE
    ) {
      const { href, target } = attribs ?? {};
      const isItalic =
        children[0]?.parent?.name === HTML_PARSER.NAME_EM ||
        children[0]?.parent?.parent?.name === HTML_PARSER.NAME_EM;

      if (
        !doesAnyDomNodeParentContainName(parent, HTML_PARSER.NAME_TABLE_CELL) &&
        isUrlValidForInternalUsage(href)
      ) {
        return (
          <NextLink
            href={toInternalLink(sanitize(href))}
            prefetch={false}
            passHref
          >
            <ArticleLink italic={isItalic} target={target}>
              {domToReact(children, articleTextOptions)}
            </ArticleLink>
          </NextLink>
        );
      }

      return (
        <ArticleLink
          href={forceHttps(href)}
          italic={isItalic}
          rel="noreferrer noopener"
          target="_blank"
        >
          {domToReact(children, articleTextOptions)}
        </ArticleLink>
      );
    }

    if (name === HTML_PARSER.NAME_FIGURE && children[0]?.attribs) {
      return (
        <figure className="w-full">
          {domToReact(children, articleTextOptions)}
        </figure>
      );
    }

    if (
      name === HTML_PARSER.NAME_SPAN &&
      parent?.name === HTML_PARSER.NAME_HREF &&
      children[0]?.data
    ) {
      return children[0].data;
    }

    if (
      (name === HTML_PARSER.NAME_DIV &&
        children?.[0]?.data &&
        isYoutubeIframe(children[0].data)) ||
      (name === HTML_PARSER.NAME_PARAGRAPH &&
        children?.[0]?.data &&
        isYoutubeIframe(children[0].data)) ||
      (name === HTML_PARSER.NAME_IFRAME &&
        attribs?.src &&
        isYoutubeIframe(attribs.src))
    ) {
      const url = children?.[0]?.data?.trim() || attribs?.src;
      const videoId = getYTVideoId(url);

      if (videoId) {
        const embedUrl = `https://www.youtube.com/embed/${videoId}?${THIRD_PARTY.YT_PARAMS}`;

        return (
          <YoutubeFacade
            className="my-6"
            src={embedUrl}
            title={attribs.title}
            full
          />
        );
      }
    }

    if (
      name === HTML_PARSER.NAME_VIDEO &&
      attribs?.src?.includes('instagram.com')
    ) {
      return <InstagramEmbed className="my-6" url={attribs.src} />;
    }

    if (!attribs || children.length === 0) {
      return null;
    }

    if (name === HTML_PARSER.NAME_SPAN && attribs.style) {
      const hasNewLineAtTheStartOrEnd = /^\/n\/n/.test(next?.data);

      if (
        !hasNewLineAtTheStartOrEnd &&
        [
          HTML_PARSER.NAME_PARAGRAPH,
          HTML_PARSER.NAME_LIST_ITEM,
          HTML_PARSER.NAME_ITALIC,
        ].includes(parent?.name)
      ) {
        return domToReact(children, articleTextOptions);
      }

      return (
        <Text
          as="p"
          className={twJoin(
            'break-words',
            !hasNewLineAtTheStartOrEnd && 'mb-4'
          )}
        >
          {domToReact(children, articleTextOptions)}
        </Text>
      );
    }

    if (
      attribs?.class === HTML_PARSER.EMBED_VIDEO_BLOCK_CLASS_ATTRIBUTE &&
      isTwitterUrlValid(children[0]?.data)
    ) {
      const tweetUrl = children[0].data;
      const tweetId = tweetUrl
        .substring(tweetUrl.lastIndexOf('/') + 1, tweetUrl.length)
        .trim();

      // If we retrieve the tweetId correctly, it has to be parsed
      // as a integer
      if (Number.isInteger(Number(tweetId))) {
        return <ArticleTweet tweetId={tweetId} />;
      }

      return <></>;
    }

    /**
     * Recently was removed a series of custom components that are rendered when
     * the article text has a specific HTML markup or a custom HTML tag. To
     * resolve the retroative we skip and render nothing instead.
     */
    if (
      attribs?.class?.includes(HTML_PARSER.WISHLIST_KEYWORD_CLASS_ATTRIBUTE) ||
      attribs?.class?.includes(
        HTML_PARSER.WISHLIST_KEYWORD_BLOCK_CLASS_ATTRIBUTE
      ) ||
      name === HTML_PARSER.NAME_PRODUCT_RANKING_TABLE
    ) {
      return <></>;
    }

    return null;
  },
};

const toArticleText = (articleText) => parse(articleText, articleTextOptions);

export default toArticleText;
